<template>
  <div class="customer-financial-situation">
    <div class="financial-situation-header">
      <h5>Situação Financeira do Cliente</h5>
      <button class="close-action" type="button" @click="$emit('close')">
        <b-icon-x scale="1.5" />
      </button>
    </div>

    <div class="customer-context">
      <div class="customer-search-field">
        <InputInfinit
          label="Cliente ou CNPJ"
          typeFilter="customer"
          :dataInput.sync="idCliente"
          :isEdit="idCliente"
          :onSearch="fillCustomerComboBoxWithPaginate"
          :enableIconCreate="false"
          :filterIsEnable="false"
          outsideFilterType="start"
          @selectItem="handleCustomerSelected"
        />
      </div>

      <div v-if="selectedCustomer" class="selected-customer">
        <span>Cliente selecionado</span>
        <strong>{{ selectedCustomer.nome || "--" }}</strong>
        <small>
          {{ selectedCustomer.cpfCnpj || "CPF/CNPJ não informado" }}
          <template v-if="selectedCustomer.celular || selectedCustomer.telefone">
            • {{ selectedCustomer.celular || selectedCustomer.telefone }}
          </template>
        </small>
      </div>

      <button
        class="history-action"
        type="button"
        :disabled="!selectedCustomer || loading"
        @click="showHistory = !showHistory"
      >
        <b-icon-arrow-left v-if="showHistory" />
        <b-icon-card-list v-else />
        {{ showHistory ? "Voltar ao resumo" : "Ver histórico completo" }}
      </button>
    </div>

    <div v-if="!selectedCustomer && !loading" class="empty-state">
      Selecione um cliente para visualizar a situação financeira.
    </div>

    <div v-else-if="loading" class="loading-state">
      <b-spinner small />
      Carregando situação financeira...
    </div>

    <template v-else>
      <div class="primary-summary">
        <div
          v-for="item in summaryCards"
          :key="item.label"
          :class="`summary-card ${item.variant}`"
        >
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <small v-if="item.helper">{{ item.helper }}</small>
        </div>
      </div>

      <div v-if="!showHistory" class="installments-grid">
        <div class="installments-card">
          <div class="installments-header">
            <h6>Parcelas em aberto ({{ openInstallments.length }})</h6>
          </div>

          <div v-if="!openInstallments.length" class="table-empty-state">
            Nenhuma parcela a vencer.
          </div>

          <div v-else class="installments-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Vencimento</th>
                  <th>Parcela</th>
                  <th>Valor</th>
                  <th>Forma de pagamento</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="bill in openInstallments" :key="bill.id">
                  <td>{{ formatDate(bill.data) }}</td>
                  <td>{{ formatInstallment(bill) }}</td>
                  <td>{{ formatMoney(bill.valorRestante) }}</td>
                  <td>{{ bill.formaPagamento || "--" }}</td>
                  <td>
                    <button
                      class="row-action"
                      type="button"
                      :disabled="loadingBillDetails === bill.id"
                      @click="openBillDetails(bill)"
                    >
                      <b-spinner v-if="loadingBillDetails === bill.id" small />
                      <b-icon-chevron-right v-else />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="installments-total open-total">
            <span>Total a vencer</span>
            <strong>{{ formatMoney(openInstallmentsTotal) }}</strong>
          </div>
        </div>

        <div class="installments-card overdue-card">
          <div class="installments-header">
            <h6>Parcelas vencidas ({{ overdueInstallments.length }})</h6>
          </div>

          <div v-if="!overdueInstallments.length" class="table-empty-state">
            Nenhuma parcela vencida.
          </div>

          <div v-else class="installments-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Vencimento</th>
                  <th>Parcela</th>
                  <th>Valor</th>
                  <th>Dias de atraso</th>
                  <th>Forma de pagamento</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="bill in overdueInstallments" :key="bill.id">
                  <td class="overdue-value">{{ formatDate(bill.data) }}</td>
                  <td class="overdue-value">{{ formatInstallment(bill) }}</td>
                  <td class="overdue-value">{{ formatMoney(bill.valorRestante) }}</td>
                  <td class="overdue-value">{{ getDaysOverdue(bill.data) }} dias</td>
                  <td>{{ bill.formaPagamento || "--" }}</td>
                  <td>
                    <button
                      class="row-action overdue"
                      type="button"
                      :disabled="loadingBillDetails === bill.id"
                      @click="openBillDetails(bill)"
                    >
                      <b-spinner v-if="loadingBillDetails === bill.id" small />
                      <b-icon-chevron-right v-else />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="installments-total overdue-total">
            <span>Total vencido</span>
            <strong>{{ formatMoney(overdueInstallmentsTotal) }}</strong>
          </div>
        </div>
      </div>

      <div v-else class="bills-table-card">
        <div class="bills-table-header">
          <h6>Histórico completo</h6>
          <button type="button" class="refresh-action" @click="fetchFinancialSituation">
            <b-icon-arrow-clockwise />
            Atualizar
          </button>
        </div>

        <div v-if="!bills.length" class="empty-state compact">
          Nenhuma conta encontrada para este cliente.
        </div>

        <div v-else class="bills-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Referência</th>
                <th>Situação</th>
                <th>Forma</th>
                <th>Consome limite</th>
                <th>Valor</th>
                <th>Pago</th>
                <th>Restante</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="bill in bills" :key="bill.id">
                <td>{{ formatDate(bill.data) }}</td>
                <td>
                  <strong>{{ formatReference(bill) }}</strong>
                  <span>{{ bill.descricao || "--" }}</span>
                </td>
                <td>
                  <span :class="`bill-status ${getStatusClass(bill)}`">
                    {{ bill.situacao }}
                  </span>
                </td>
                <td>{{ bill.formaPagamento || "--" }}</td>
                <td>
                  <span
                    :class="`credit-consumption ${Number(bill.consumirLimiteCredito) === 1 ? 'enabled' : 'disabled'}`"
                  >
                    {{ Number(bill.consumirLimiteCredito) === 1 ? "Sim" : "Não" }}
                  </span>
                </td>
                <td>{{ formatMoney(bill.valorTotal) }}</td>
                <td>{{ formatMoney(bill.valorTotalRegistros || bill.valorPago) }}</td>
                <td>{{ formatMoney(bill.valorRestante) }}</td>
                <td>
                  <button
                    class="bill-action"
                    type="button"
                    :disabled="loadingBillDetails === bill.id"
                    @click="openBillDetails(bill)"
                  >
                    <b-spinner v-if="loadingBillDetails === bill.id" small />
                    <template v-else>
                      {{ Number(bill.valorRestante || 0) > 0 ? "Baixar" : "Detalhes" }}
                    </template>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="credit-overview">
        <div v-for="item in creditOverview" :key="item.label">
          <span>{{ item.label }}</span>
          <strong :class="item.variant">{{ item.value }}</strong>
        </div>
      </div>

      <div
        v-if="showAutomaticPayment && billsAvailableForAutomaticPayment.length"
        class="automatic-payment-card"
      >
        <div class="automatic-payment-description">
          <strong>Novo recebimento</strong>
          <small>
            Informe o valor recebido. As contas mais antigas serão quitadas
            primeiro e a última poderá receber uma baixa parcial.
          </small>
        </div>

        <div class="automatic-payment-action">
          <InputSelect
            label="Forma de pagamento"
            valueField="id"
            textField="nome"
            :items="paymentTypes"
            :dataSelect.sync="automaticPaymentTypeId"
            :enableIconCreate="false"
            :isDisabled="loadingAutomaticPayment"
            @changed="handleAutomaticPaymentType"
            ref="automaticPaymentType"
          />
          <InputSelect
            label="Conta de recebimento"
            valueField="id"
            textField="nome"
            :items="receiptAccounts"
            :dataSelect.sync="automaticPaymentAccountId"
            :enableIconCreate="false"
            :isDisabled="automaticPaymentAccountBlocked"
            ref="automaticPaymentAccount"
          />
          <InputMoney
            label="Valor recebido"
            type="text"
            :dataInput.sync="automaticPaymentValue"
            :isDisabled="loadingAutomaticPayment"
            labelFixed
            isSelectAll
            ref="automaticPaymentValue"
          />
          <button
            type="button"
            class="automatic-payment-button"
            :disabled="loadingAutomaticPayment"
            @click="receiveBillsByValue"
          >
            <b-spinner v-if="loadingAutomaticPayment" small />
            <span v-else>Receber valor</span>
          </button>
        </div>

        <small class="automatic-payment-balance">
          Saldo em aberto: {{ formatMoney(automaticPaymentOpenValue) }}
        </small>
      </div>

      <div class="financial-situation-footer">
        <div class="footer-primary-actions">
          <button
            class="footer-action"
            type="button"
            :disabled="!billsAvailableForAutomaticPayment.length"
            @click="showAutomaticPayment = !showAutomaticPayment"
          >
            <b-icon-cash-stack />
            {{ showAutomaticPayment ? "Ocultar recebimento" : "Novo recebimento" }}
          </button>
          <button class="footer-action" type="button" @click="openNewReceivable">
            <b-icon-file-earmark-plus />
            Nova conta a receber
          </button>
        </div>

        <button class="footer-action close-footer-action" type="button" @click="$emit('close')">
          <b-icon-x scale="1.3" />
          Fechar
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import InputInfinit from "../UI/CustomInputs/InputInfinit.vue";
import InputMoney from "../UI/CustomInputs/InputMoney.vue";
import InputSelect from "../UI/CustomInputs/InputSelect.vue";
import ServiceBill from "../../services/serviceBill";
import ServiceCustomer from "../../services/serviceCustomer";
import ServicePaymentForm from "../../services/servicePaymentForm";
import ServiceAccountReceiptBill from "../../services/serviceAccountReceiptBill";
import { mapState } from "vuex";

export default {
  name: "CustomerFinancialSituation",
  components: {
    InputInfinit,
    InputMoney,
    InputSelect,
  },
  data() {
    return {
      idCliente: "",
      selectedCustomer: null,
      financialSituation: null,
      loading: false,
      loadingBillDetails: "",
      automaticPaymentValue: 0,
      loadingAutomaticPayment: false,
      automaticPaymentTypeId: "",
      automaticPaymentAccountId: "",
      automaticPaymentAccountBlocked: false,
      paymentTypes: [],
      receiptAccounts: [],
      showHistory: false,
      showAutomaticPayment: false,
    };
  },
  computed: {
    ...mapState({
      dataConfig: (state) => state.dataConfig,
    }),
    bills() {
      return this.financialSituation?.bills || [];
    },
    summary() {
      return this.financialSituation?.summary || {};
    },
    billsAvailableForAutomaticPayment() {
      return this.bills.filter(
        (bill) =>
          Number(bill.valorRestante) > 0 && bill.situacao !== "Perda"
      );
    },
    openInstallments() {
      return this.billsAvailableForAutomaticPayment.filter(
        (bill) => !this.isOverdueBill(bill)
      );
    },
    overdueInstallments() {
      return this.billsAvailableForAutomaticPayment.filter((bill) =>
        this.isOverdueBill(bill)
      );
    },
    openInstallmentsTotal() {
      return this.sumRemainingValue(this.openInstallments);
    },
    overdueInstallmentsTotal() {
      return this.sumRemainingValue(this.overdueInstallments);
    },
    automaticPaymentOpenValue() {
      return Number(
        this.billsAvailableForAutomaticPayment
          .reduce(
            (total, bill) => total + Number(bill.valorRestante || 0),
            0
          )
          .toFixed(2)
      );
    },
    isSimpleSafe() {
      return this.dataConfig?.caixa_simples ?? true;
    },
    summaryCards() {
      return [
        {
          label: "Total em compras",
          value: this.formatMoney(this.summary.totalPurchases),
          helper: `${this.summary.quantityPurchases || 0} compra(s)`,
          variant: "neutral",
        },
        {
          label: "Em aberto",
          value: this.formatMoney(this.summary.totalOpen),
          helper: `${this.summary.quantityOpen || 0} conta(s)`,
          variant: "warning",
        },
        {
          label: "Vencidas",
          value: this.formatMoney(this.summary.totalOverdue),
          helper: `${this.summary.quantityOverdue || 0} conta(s)`,
          variant: "danger",
        },
        {
          label: "Recebido",
          value: this.formatMoney(this.summary.totalPaid),
          helper: `${this.summary.quantityPaid || 0} conta(s)`,
          variant: "success",
        },
        {
          label: "Último pagamento",
          value: this.summary.lastPayment
            ? this.formatDate(this.summary.lastPayment.dataPagamento)
            : "--",
          helper: this.formatLastPaymentValue(this.summary.lastPayment),
          variant: "neutral",
        },
      ];
    },
    creditOverview() {
      const availableCredit = this.summary.availableCredit;
      const creditUsed = Number(this.summary.creditUsed || 0);
      const creditLimit = Number(this.summary.creditLimit || 0);

      return [
        {
          label: "Limite de crédito",
          value: this.formatOptionalMoney(this.summary.creditLimit),
          variant: "",
        },
        {
          label: "Disponível",
          value: this.formatOptionalMoney(availableCredit),
          variant:
            availableCredit !== null && Number(availableCredit) < 0
              ? "danger"
              : "success",
        },
        {
          label: "Utilizado",
          value: this.formatMoney(creditUsed),
          variant: creditUsed > creditLimit ? "danger" : "",
        },
        {
          label: "% utilizado",
          value:
            this.summary.creditUsagePercentage === null ||
            this.summary.creditUsagePercentage === undefined
              ? "--"
              : `${this.summary.creditUsagePercentage}%`,
          variant: creditUsed > creditLimit ? "danger" : "",
        },
        {
          label: "Cliente desde",
          value: this.formatCustomerSince(this.selectedCustomer.createdAt),
          variant: "primary",
        },
      ];
    },
  },
  methods: {
    async fillCustomerComboBoxWithPaginate(id, q, filterType, page) {
      try {
        return await ServiceCustomer.fillComboBoxWithPaginate(
          id,
          q,
          filterType,
          page
        );
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Ocorreu um problema ao listar os clientes",
          type: "info",
        });
      }
    },
    async handleCustomerSelected(customer) {
      this.selectedCustomer = customer || null;
      this.financialSituation = null;

      if (customer?.id) {
        await this.fetchFinancialSituation();
      }
    },
    async fetchFinancialSituation() {
      try {
        this.loading = true;
        this.financialSituation = await ServiceBill.getCustomerFinancialSituation(
          this.selectedCustomer.id
        );
        this.selectedCustomer = this.financialSituation.customer || this.selectedCustomer;
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Ocorreu um problema ao buscar a situação financeira do cliente",
          type: "info",
        });
      } finally {
        this.loading = false;
      }
    },
    async openBillDetails(bill) {
      try {
        this.loadingBillDetails = bill.id;
        const detailedBill = await ServiceBill.findDetailedBillById(bill.id);
        detailedBill.totalContas = bill.totalContas;
        this.$emit("open-bill-details", detailedBill);
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Ocorreu um problema ao abrir os detalhes da conta",
          type: "info",
        });
      } finally {
        this.loadingBillDetails = "";
      }
    },
    openNewReceivable() {
      this.$emit("open-new-receivable", this.selectedCustomer);
    },
    async loadAutomaticPaymentOptions() {
      try {
        const [paymentTypes, receiptAccounts] = await Promise.all([
          ServicePaymentForm.getAllPayment(),
          ServiceAccountReceiptBill.fillComobobox(),
        ]);

        this.paymentTypes = paymentTypes.sort((a, b) => a.cod - b.cod);
        this.receiptAccounts = receiptAccounts;

        if (this.isSimpleSafe) {
          this.selectDrawerCashAccount(true);
        }
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Ocorreu um problema ao carregar as opções de recebimento",
          type: "info",
        });
      }
    },
    selectDrawerCashAccount(blockAccount = false) {
      const drawerCashAccount = this.receiptAccounts.find(
        (account) => account.nome === "CAIXA GAVETA"
      );

      this.automaticPaymentAccountBlocked = blockAccount;
      this.automaticPaymentAccountId = drawerCashAccount?.id || "";

      if (this.$refs.automaticPaymentAccount) {
        this.$refs.automaticPaymentAccount.valueItems =
          this.automaticPaymentAccountId;
      }
    },
    handleAutomaticPaymentType() {
      const paymentType = this.paymentTypes.find(
        (payment) => payment.id === this.automaticPaymentTypeId
      );

      if (this.isSimpleSafe || paymentType?.tipo === "Dinheiro") {
        this.selectDrawerCashAccount(true);
        return;
      }

      this.automaticPaymentAccountBlocked = false;
      const drawerCashAccount = this.receiptAccounts.find(
        (account) => account.nome === "CAIXA GAVETA"
      );

      if (this.automaticPaymentAccountId === drawerCashAccount?.id) {
        this.automaticPaymentAccountId = "";
        if (this.$refs.automaticPaymentAccount) {
          this.$refs.automaticPaymentAccount.valueItems = "";
        }
      }
    },
    resetAutomaticPayment() {
      this.automaticPaymentValue = 0;
      if (this.$refs.automaticPaymentValue) {
        this.$refs.automaticPaymentValue.valueInput = 0;
      }
    },
    async receiveBillsByValue() {
      const value = Number(this.automaticPaymentValue);

      if (!Number.isFinite(value) || value <= 0) {
        return this.$toast.open({
          message: "Informe um valor maior que zero",
          type: "info",
        });
      }

      if (value > this.automaticPaymentOpenValue) {
        return this.$toast.open({
          message: `O valor informado não pode ser maior que o saldo em aberto de ${this.formatMoney(
            this.automaticPaymentOpenValue
          )}`,
          type: "info",
        });
      }

      if (!this.automaticPaymentTypeId) {
        return this.$toast.open({
          message: "Selecione a forma de pagamento",
          type: "info",
        });
      }

      if (!this.automaticPaymentAccountId) {
        return this.$toast.open({
          message: "Selecione a conta de recebimento",
          type: "info",
        });
      }

      try {
        this.loadingAutomaticPayment = true;
        const ids = this.billsAvailableForAutomaticPayment.map(
          (bill) => bill.id
        );
        const result = await ServiceBill.launchMultiplePaymentsByValue(
          ids,
          value,
          this.automaticPaymentTypeId,
          this.automaticPaymentAccountId
        );

        await this.fetchFinancialSituation();
        this.resetAutomaticPayment();
        this.$emit("financial-updated");

        return this.$toast.open({
          message: `Valor recebido em ${result.paymentsCount} parcela(s)`,
          type: "success",
        });
      } catch (error) {
        return this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Ocorreu um erro ao realizar a baixa por valor",
          type: "info",
        });
      } finally {
        this.loadingAutomaticPayment = false;
      }
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatOptionalMoney(value) {
      if (value === null || value === undefined) {
        return "--";
      }

      return this.formatMoney(value);
    },
    formatLastPaymentValue(lastPayment) {
      if (!lastPayment) return "Nenhum recebimento registrado";

      const paymentType = lastPayment.formaPagamento
        ? ` (${lastPayment.formaPagamento})`
        : "";

      return `${this.formatMoney(lastPayment.valor)}${paymentType}`;
    },
    formatDate(date) {
      if (!date) return "--";

      return new Date(`${date}T00:00:00`).toLocaleDateString("pt-BR");
    },
    formatReference(bill) {
      if (bill.numVenda) return `Venda ${bill.numVenda}`;
      if (bill.numVendaMtq) return `Venda MT² ${bill.numVendaMtq}`;
      if (bill.codigoReferencia) return bill.codigoReferencia;

      return `Conta ${bill.numeracao || ""}`.trim();
    },
    formatInstallment(bill) {
      const installment = bill.numeracao || "--";
      const total = bill.totalContas || "--";

      return `${installment}/${total}`;
    },
    formatCustomerSince(createdAt) {
      if (!createdAt) return "--";

      return this.formatDate(String(createdAt).slice(0, 10));
    },
    isOverdueBill(bill) {
      if (!bill?.data || Number(bill.valorRestante || 0) <= 0) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(`${bill.data}T00:00:00`);

      return dueDate < today;
    },
    getDaysOverdue(date) {
      if (!date) return 0;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(`${date}T00:00:00`);

      return Math.max(
        0,
        Math.floor((today.getTime() - dueDate.getTime()) / 86400000)
      );
    },
    sumRemainingValue(bills) {
      return Number(
        bills
          .reduce(
            (total, bill) => total + Number(bill.valorRestante || 0),
            0
          )
          .toFixed(2)
      );
    },
    getStatusClass(bill) {
      if (bill.situacao === "Pago") return "paid";
      if (bill.situacao === "Perda") return "loss";
      if (Number(bill.valorRestante || 0) > 0) return "open";

      return "neutral";
    },
  },
  mounted() {
    this.loadAutomaticPaymentOptions();
  },
};
</script>

<style scoped>
.customer-financial-situation {
  padding: 0;
  color: #10182b;
  background: #fff;
}

.financial-situation-header {
  position: sticky;
  top: 0;
  z-index: 5;
  min-height: 60px;
  padding: 0 28px;
  border-bottom: 1px solid #e6e9ef;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.financial-situation-header h5 {
  margin: 0;
  color: #10182b;
  font-family: Poppins-Bold, sans-serif !important;
  font-size: 18px;
}

.close-action {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #10182b;
}

.close-action:hover {
  background: #f3f5f8;
}

.customer-context {
  display: grid;
  grid-template-columns: minmax(300px, 0.9fr) minmax(360px, 1.4fr) auto;
  align-items: center;
  gap: 28px;
  min-height: 110px;
  padding: 18px 28px;
  border-bottom: 1px solid #e6e9ef;
}

.customer-search-field {
  min-width: 0;
}

.selected-customer {
  min-width: 0;
  padding-left: 28px;
  border-left: 1px solid #e6e9ef;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.selected-customer span,
.selected-customer small {
  color: #50596b;
  font-size: 12px;
}

.selected-customer strong {
  overflow: hidden;
  color: #10182b;
  font-family: Poppins-Bold, sans-serif !important;
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-action,
.footer-action {
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid #d8dde6;
  border-radius: 6px;
  background: #fff;
  color: #10182b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: Poppins-Regular, sans-serif !important;
  font-size: 12px;
  transition: 0.2s ease;
}

.history-action:hover:not(:disabled),
.footer-action:hover:not(:disabled) {
  border-color: #25156f;
  color: #25156f;
  background: #f7f5ff;
}

.history-action:disabled,
.footer-action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.loading-state,
.empty-state {
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #687286;
  font-family: Poppins-Bold, sans-serif !important;
}

.empty-state.compact {
  min-height: 120px;
}

.primary-summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;
  padding: 20px 28px 10px;
}

.summary-card {
  min-height: 126px;
  padding: 20px 14px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  border-color: #dfe3ea;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.summary-card span {
  color: #222b3b;
  font-size: 11px;
  text-transform: uppercase;
}

.summary-card strong {
  margin: 5px 0 2px;
  color: #111827;
  font-family: Poppins-Bold, sans-serif !important;
  font-size: clamp(17px, 1.45vw, 23px);
}

.summary-card small {
  color: #343c4c;
  font-size: 11px;
}

.summary-card.warning strong {
  color: #111827;
}

.summary-card.success strong {
  color: #119640;
}

.summary-card.danger strong {
  color: #d11616;
}

.installments-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
  gap: 18px;
  padding: 10px 28px;
}

.installments-card {
  min-width: 0;
  min-height: 295px;
  border: 1px solid #dfe3ea;
  border-radius: 7px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.installments-header {
  min-height: 46px;
  padding: 0 18px;
  border-bottom: 1px solid #dfe3ea;
  display: flex;
  align-items: center;
}

.installments-header h6 {
  margin: 0;
  color: #151d2c;
  font-family: Poppins-Bold, sans-serif !important;
  font-size: 13px;
}

.overdue-card .installments-header h6 {
  color: #d11616;
}

.installments-table-wrapper {
  flex: 1;
  max-height: 245px;
  padding: 0 12px;
  overflow: auto;
}

.installments-table-wrapper table {
  width: 100%;
  border-collapse: collapse;
}

.installments-table-wrapper th,
.installments-table-wrapper td {
  padding: 9px 8px;
  border-bottom: 1px solid #e7eaf0;
  text-align: left;
  white-space: nowrap;
}

.installments-table-wrapper th {
  color: #30394a;
  font-size: 10px;
  font-weight: 600;
}

.installments-table-wrapper td {
  color: #263044;
  font-size: 11px;
}

.installments-table-wrapper th:last-child,
.installments-table-wrapper td:last-child {
  width: 28px;
  padding-right: 0;
  text-align: right;
}

.row-action {
  width: 25px;
  height: 25px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #eef2f8;
  color: #1f3b78;
}

.row-action.overdue {
  color: #c91a1a;
  background: #fff0f0;
}

.overdue-value {
  color: #d11616 !important;
}

.table-empty-state {
  flex: 1;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #727b8d;
  font-size: 12px;
}

.installments-total {
  min-height: 50px;
  margin: 0 12px;
  padding: 0 8px;
  border-top: 1px solid #dfe3ea;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
}

.installments-total strong {
  font-family: Poppins-Bold, sans-serif !important;
  font-size: 14px;
}

.open-total {
  color: #173a99;
}

.overdue-total {
  color: #d11616;
}

.bills-table-card {
  margin: 10px 28px;
}

.bills-table-wrapper {
  max-height: 295px;
}

.credit-overview {
  min-height: 86px;
  margin: 8px 28px 10px;
  border: 1px solid #dfe3ea;
  border-radius: 7px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: center;
}

.credit-overview > div {
  min-width: 0;
  padding: 5px 18px;
  border-right: 1px solid #dfe3ea;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  text-align: center;
}

.credit-overview > div:last-child {
  border-right: none;
}

.credit-overview span {
  color: #354054;
  font-size: 11px;
}

.credit-overview strong {
  color: #171f2f;
  font-family: Poppins-Bold, sans-serif !important;
  font-size: 14px;
}

.credit-overview strong.success {
  color: #119640;
}

.credit-overview strong.danger {
  color: #d11616;
}

.credit-overview strong.primary {
  color: #173a99;
}

.automatic-payment-card {
  padding: 14px 16px;
  border: 1px solid #d9d3f0;
  border-radius: 8px;
  margin: 14px 28px;
  border-color: #d9d3f0;
  background: #faf9ff;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.automatic-payment-description {
  display: flex;
  flex: 1 1 320px;
  flex-direction: column;
  color: #24345f;
}

.automatic-payment-description small,
.automatic-payment-balance {
  color: #63708f;
}

.automatic-payment-action {
  display: flex;
  flex: 1 1 680px;
  align-items: center;
  gap: 8px;
}

.automatic-payment-action .input-group,
.automatic-payment-action .selectPlus {
  flex: 1;
}

.automatic-payment-button {
  min-width: 125px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #004727;
  color: #fff;
  font-family: Poppins-Bold, sans-serif !important;
  font-size: 11px;
}

.automatic-payment-button:disabled {
  background: #b7bdc4;
  cursor: not-allowed;
}

.automatic-payment-balance {
  flex-basis: 100%;
  text-align: right;
}

.bills-table-card {
  border: 1px solid #e6e8f3;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.bills-table-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e6e8f3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.bills-table-header h6 {
  margin: 0;
  font-family: Poppins-Bold, sans-serif !important;
}

.refresh-action,
.bill-action {
  border: none;
  border-radius: 6px;
  font-family: Poppins-Bold, sans-serif !important;
}

.refresh-action {
  height: 32px;
  padding: 0 12px;
  background: #f3f4ff;
  color: #1a1b66;
}

.bills-table-wrapper {
  overflow: auto;
}

.bills-table-wrapper table {
  width: 100%;
  border-collapse: collapse;
}

.bills-table-wrapper th,
.bills-table-wrapper td {
  padding: 10px 12px;
  border-bottom: 1px solid #edf0f6;
  text-align: left;
  vertical-align: middle;
}

.bills-table-wrapper th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #34434d;
  color: #fff;
}

.bills-table-wrapper td {
  color: #00124f;
}

.bills-table-wrapper td span {
  display: block;
  max-width: 260px;
  margin-top: 3px;
  overflow: hidden;
  color: #5f638e;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bill-status,
.credit-consumption {
  display: inline-flex !important;
  padding: 4px 8px;
  border-radius: 999px;
  font-family: Poppins-Bold, sans-serif !important;
  font-size: 11px !important;
}

.bill-status.paid {
  color: #009843 !important;
  background: #e9f8ef;
}

.bill-status.open {
  color: #d90000 !important;
  background: #fff0f0;
}

.bill-status.loss {
  color: #777 !important;
  background: #eee;
}

.credit-consumption.enabled {
  color: #8a5700 !important;
  background: #fff5dc;
}

.credit-consumption.disabled {
  color: #5f638e !important;
  background: #f0f1f6;
}

.bill-action {
  min-width: 86px;
  min-height: 30px;
  background: #005c80;
  color: #fff;
}

.bill-action:disabled {
  opacity: 0.75;
}

.financial-situation-footer {
  min-height: 72px;
  padding: 12px 28px;
  border-top: 1px solid #e6e9ef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.footer-primary-actions {
  display: flex;
  align-items: center;
  gap: 18px;
}

.footer-action {
  min-width: 200px;
}

.close-footer-action {
  min-width: 125px;
}

@media (max-width: 1180px) {
  .customer-context {
    grid-template-columns: minmax(280px, 1fr) minmax(320px, 1fr);
  }

  .history-action {
    grid-column: 1 / -1;
    justify-self: end;
  }

  .primary-summary {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .installments-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .financial-situation-header,
  .customer-context,
  .primary-summary,
  .installments-grid,
  .financial-situation-footer {
    padding-left: 16px;
    padding-right: 16px;
  }

  .customer-context {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .selected-customer {
    padding-top: 14px;
    padding-left: 0;
    border-top: 1px solid #e6e9ef;
    border-left: none;
  }

  .history-action {
    width: 100%;
  }

  .primary-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .credit-overview {
    margin-left: 16px;
    margin-right: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .credit-overview > div {
    min-height: 68px;
    border-bottom: 1px solid #dfe3ea;
  }

  .automatic-payment-card,
  .bills-table-card {
    margin-left: 16px;
    margin-right: 16px;
  }

  .automatic-payment-action,
  .financial-situation-footer,
  .footer-primary-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .footer-action,
  .close-footer-action {
    width: 100%;
  }
}
</style>
